import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/components/AuthProvider";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, X, MoreHorizontal, Filter, Activity as ActivityIcon, Trash2, Pencil, Star, LayoutGrid, Grid2X2, GanttChartSquare, Share2, Users, Info, LayoutTemplate, Mail } from "lucide-react";
import { cn } from "@/lib/utils";
import { gradient80For } from "@/lib/boardColors";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import CardTile from "@/components/board/CardTile";
import CardModal from "@/components/board/CardModal";
import BoardFilters from "@/components/board/BoardFilters";
import ActivitySheet from "@/components/board/ActivitySheet";
import BoardMatrix from "@/components/board/BoardMatrix";
import BoardSprints from "@/components/board/BoardSprints";
import ShareBoardDialog from "@/components/dialogs/ShareBoardDialog";
import BoardNotifSettingsDialog from "@/components/board/BoardNotifSettingsDialog";
import BoardDetailsDialog from "@/components/dialogs/BoardDetailsDialog";
import NadIAPanel from "@/components/board/NadIAPanel";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

function positionAfter(prev, next) {
  if (prev == null && next == null) return 1000;
  if (prev == null) return next / 2;
  if (next == null) return prev + 1000;
  return (prev + next) / 2;
}

export default function BoardView() {
  const { boardId } = useParams();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [newListName, setNewListName] = useState("");
  const [addingList, setAddingList] = useState(false);
  const [openCard, setOpenCard] = useState(null);
  const [filters, setFilters] = useState({ q: "", labelIds: [], priority: "", dueRange: "", onlyMine: false });
  const [activityOpen, setActivityOpen] = useState(false);
  const [viewMode, setViewMode] = useState("kanban"); // 'kanban' | 'matrix' | 'sprints'
  const [shareOpen, setShareOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [nadiaOpen, setNadiaOpen] = useState(false);
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["board-full", boardId],
    queryFn: async () => (await api.get(`/boards/${boardId}/full`)).data,
  });

  // Open card if ?card= query param
  useEffect(() => {
    const cid = searchParams.get("card");
    if (cid) setOpenCard(cid);
  }, [searchParams]);

  const board = data?.board;
  const lists = useMemo(() => (data?.lists || []).slice().sort((a, b) => a.position - b.position), [data]);
  const cards = data?.cards || [];
  const labels = data?.labels || [];

  const cardsByList = useMemo(() => {
    const map = {};
    for (const l of lists) map[l.list_id] = [];
    for (const c of cards) {
      if (!map[c.list_id]) map[c.list_id] = [];
      map[c.list_id].push(c);
    }
    Object.keys(map).forEach(k => map[k].sort((a, b) => a.position - b.position));
    return map;
  }, [lists, cards]);

  // Filtering function (only applied for display; does not mutate persisted data)
  const filterCards = useCallback((cs) => {
    return cs.filter((c) => {
      if (filters.q && !(`${c.title} ${c.description || ""}`.toLowerCase().includes(filters.q.toLowerCase()))) return false;
      if (filters.labelIds.length > 0 && !filters.labelIds.some(l => (c.labels || []).includes(l))) return false;
      if (filters.priority && c.priority !== filters.priority) return false;
      if (filters.dueRange) {
        if (!c.due_date) return false;
        const due = new Date(c.due_date);
        const now = new Date();
        if (filters.dueRange === "overdue" && due >= now) return false;
        if (filters.dueRange === "week") {
          const w = new Date(); w.setDate(w.getDate() + 7);
          if (due > w || due < now) return false;
        }
      }
      return true;
    });
  }, [filters]);

  // ---------- Mutations ----------
  const createListMut = useMutation({
    mutationFn: async (name) => (await api.post(`/boards/${boardId}/lists`, { name })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["board-full", boardId] });
      setNewListName(""); setAddingList(false);
    },
  });

  const deleteListMut = useMutation({
    mutationFn: async (listId) => (await api.delete(`/lists/${listId}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["board-full", boardId] }),
  });

  const renameListMut = useMutation({
    mutationFn: async ({ listId, name }) => (await api.patch(`/lists/${listId}`, { name })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["board-full", boardId] }),
  });

  const createCardMut = useMutation({
    mutationFn: async ({ listId, title }) => (await api.post(`/lists/${listId}/cards`, { title })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["board-full", boardId] }),
  });

  // ---------- DnD handler ----------
  const onDragEnd = async (result) => {
    const { type, source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (type === "LIST") {
      // Optimistic update for list order
      const newLists = lists.slice();
      const [moved] = newLists.splice(source.index, 1);
      newLists.splice(destination.index, 0, moved);
      const prev = newLists[destination.index - 1]?.position;
      const next = newLists[destination.index + 1]?.position;
      const newPos = positionAfter(prev, next);
      qc.setQueryData(["board-full", boardId], (old) => {
        if (!old) return old;
        return { ...old, lists: old.lists.map(l => l.list_id === moved.list_id ? { ...l, position: newPos } : l) };
      });
      try {
        await api.post(`/boards/${boardId}/lists/reorder`, { list_id: moved.list_id, position: newPos });
      } catch {
        toast.error(t("board.reorderFail"));
        qc.invalidateQueries({ queryKey: ["board-full", boardId] });
      }
      return;
    }

    // CARD
    if (type === "CARD") {
      const srcListId = source.droppableId;
      const dstListId = destination.droppableId;
      const srcCards = (cardsByList[srcListId] || []).slice();
      const dstCards = srcListId === dstListId ? srcCards : (cardsByList[dstListId] || []).slice();
      const [moved] = srcCards.splice(source.index, 1);
      if (srcListId === dstListId) {
        srcCards.splice(destination.index, 0, moved);
        const prev = srcCards[destination.index - 1]?.position;
        const next = srcCards[destination.index + 1]?.position;
        const newPos = positionAfter(prev, next);
        qc.setQueryData(["board-full", boardId], (old) => {
          if (!old) return old;
          return { ...old, cards: old.cards.map(c => c.card_id === moved.card_id ? { ...c, position: newPos } : c) };
        });
        try {
          await api.post(`/boards/${boardId}/cards/reorder`, { card_id: moved.card_id, source_list_id: srcListId, dest_list_id: dstListId, dest_position: newPos });
        } catch {
          toast.error(t("board.moveCardFail"));
          qc.invalidateQueries({ queryKey: ["board-full", boardId] });
        }
      } else {
        dstCards.splice(destination.index, 0, moved);
        const prev = dstCards[destination.index - 1]?.position;
        const next = dstCards[destination.index + 1]?.position;
        const newPos = positionAfter(prev, next);
        qc.setQueryData(["board-full", boardId], (old) => {
          if (!old) return old;
          return { ...old, cards: old.cards.map(c => c.card_id === moved.card_id ? { ...c, list_id: dstListId, position: newPos } : c) };
        });
        try {
          await api.post(`/boards/${boardId}/cards/reorder`, { card_id: moved.card_id, source_list_id: srcListId, dest_list_id: dstListId, dest_position: newPos });
        } catch {
          toast.error(t("board.moveCardFail"));
          qc.invalidateQueries({ queryKey: ["board-full", boardId] });
        }
      }
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex flex-col">
        <div className="px-6 py-4 border-b border-white/30 dark:border-white/10 flex items-center gap-3">
          <Skeleton className="h-7 w-48 rounded-md" />
        </div>
        <div className="flex-1 p-6 flex gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-72 w-72 rounded-2xl flex-none" />)}
        </div>
      </div>
    );
  }

  if (!board) return null;

  return (
    <div className="h-full flex flex-col" data-testid="board-view">
      {/* Board header */}
      <div className={cn(
        "px-6 py-4 border-b border-white/30 dark:border-white/10 flex items-center gap-3 flex-wrap bg-gradient-to-r",
        gradient80For(board.cover_color)
      )}>
        <h1 className="font-display text-xl md:text-2xl font-semibold tracking-tight text-white drop-shadow-sm" data-testid="board-title">{board.name}</h1>

        {board.relation === "shared_with_me" && (
          <span className="text-[10px] uppercase tracking-wider font-semibold bg-white/25 text-white px-2 py-0.5 rounded-full" data-testid="board-shared-badge">
            {t("board.sharedBadge")}
          </span>
        )}
        {board.relation === "collaborative" && (
          <span className="text-[10px] uppercase tracking-wider font-semibold bg-white/25 text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid="board-collab-badge">
            <Users className="h-3 w-3" /> {t("board.collaborativeBadge")}
          </span>
        )}

        <button
          data-testid="board-star"
          onClick={() => api.patch(`/boards/${boardId}`, { is_starred: !board.is_starred }).then(() => qc.invalidateQueries({ queryKey: ["board-full", boardId] }))}
          className="h-8 w-8 grid place-items-center rounded-full bg-white/20 hover:bg-white/30"
          aria-label={t("board.starAria")}
        >
          <Star className={cn("h-4 w-4 text-white", board.is_starred && "fill-amber-300 text-amber-300")} />
        </button>

        <div className="ml-auto flex items-center gap-2">
          <div className="glass-panel rounded-full p-0.5 inline-flex" data-testid="board-view-toggle">
            <button
              data-testid="board-view-kanban"
              onClick={() => setViewMode("kanban")}
              aria-pressed={viewMode === "kanban"}
              className={cn(
                "px-3 h-8 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition",
                viewMode === "kanban"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-white/90 hover:text-white"
              )}
            >
              <LayoutGrid className="h-3.5 w-3.5" /> {t("board.views.rolls")}
            </button>
            <button
              data-testid="board-view-matrix"
              onClick={() => setViewMode("matrix")}
              aria-pressed={viewMode === "matrix"}
              className={cn(
                "px-3 h-8 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition",
                viewMode === "matrix"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-white/90 hover:text-white"
              )}
            >
              <Grid2X2 className="h-3.5 w-3.5" /> {t("board.views.matrix")}
            </button>
            <button
              data-testid="board-view-sprints"
              onClick={() => setViewMode("sprints")}
              aria-pressed={viewMode === "sprints"}
              className={cn(
                "px-3 h-8 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition",
                viewMode === "sprints"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-white/90 hover:text-white"
              )}
            >
              <GanttChartSquare className="h-3.5 w-3.5" /> {t("board.views.sprints")}
            </button>
          </div>
          <BoardFilters filters={filters} setFilters={setFilters} labels={labels} />
          <Button
            variant="secondary"
            size="sm"
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={() => setDetailsOpen(true)}
            data-testid="board-details-button"
          >
            <Info className="h-4 w-4 mr-2" /> {t("board.details")}
          </Button>
          {currentUser?.is_admin && (
            <Button
              variant="secondary"
              size="sm"
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={async () => {
                if (!window.confirm(t("board.makeTemplateConfirm"))) return;
                try {
                  await api.post("/admin/templates/from-board", {
                    board_id: boardId,
                    name: board?.name,
                    description: board?.description,
                    category: "General",
                  });
                  toast.success(t("board.makeTemplateSuccess"));
                } catch (e) {
                  toast.error(e?.response?.data?.detail || t("board.makeTemplateFail"));
                }
              }}
              data-testid="board-make-template-button"
              title={t("board.makeTemplate")}
            >
              <LayoutTemplate className="h-4 w-4 mr-2" /> {t("board.makeTemplate")}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={() => setShareOpen(true)}
            data-testid="board-share-button"
          >
            <Share2 className="h-4 w-4 mr-2" /> {t("board.share")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={() => setActivityOpen(true)}
            data-testid="board-activity-button"
          >
            <ActivityIcon className="h-4 w-4 mr-2" /> {t("board.activity")}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="board-menu-trigger" className="h-8 w-8 grid place-items-center rounded-full bg-white/20 hover:bg-white/30 text-white">
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="glass-panel">
              <DropdownMenuLabel>{board.name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  const name = window.prompt(t("board.renamePrompt"), board.name);
                  if (name && name.trim()) {
                    await api.patch(`/boards/${boardId}`, { name });
                    qc.invalidateQueries({ queryKey: ["board-full", boardId] });
                    qc.invalidateQueries({ queryKey: ["recent-boards"] });
                  }
                }}
                data-testid="board-rename"
              >
                <Pencil className="h-4 w-4 mr-2" /> {t("board.rename")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setNotifOpen(true)}
                data-testid="board-notifications"
              >
                <Mail className="h-4 w-4 mr-2" /> {t("board.notifications", "Notificaciones por email")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={async () => {
                  if (window.confirm(t("board.deleteConfirm"))) {
                    await api.delete(`/boards/${boardId}`);
                    toast.success(t("board.deleteSuccess"));
                    navigate(`/workspaces/${board.workspace_id}`);
                  }
                }}
                className="text-rose-500"
                data-testid="board-delete"
              >
                <Trash2 className="h-4 w-4 mr-2" /> {t("board.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main content: Rolls, Matriz or Sprints */}
      <div className="flex-1 overflow-hidden">
        {viewMode === "matrix" ? (
          <BoardMatrix
            boardId={boardId}
            cards={cards.filter(c => !c.is_archived)}
            labels={labels}
            lists={lists}
            onOpenCard={(id) => setOpenCard(id)}
          />
        ) : viewMode === "sprints" ? (
          <BoardSprints
            boardId={boardId}
            cards={cards.filter(c => !c.is_archived)}
            labels={labels}
            lists={lists}
            onOpenCard={(id) => setOpenCard(id)}
          />
        ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="board" type="LIST" direction="horizontal">
            {(provided) => (
              <div
                ref={provided.innerRef} {...provided.droppableProps}
                className="h-full overflow-x-auto overflow-y-hidden kanban-scroll flex gap-4 items-start p-4 md:p-6"
                style={{ overflowAnchor: "none" }}
              >
                {lists.map((list, idx) => {
                  const listCards = filterCards(cardsByList[list.list_id] || []);
                  return (
                    <Draggable draggableId={list.list_id} index={idx} key={list.list_id}>
                      {(prov, snap) => (
                        <div
                          ref={prov.innerRef} {...prov.draggableProps}
                          className={cn(
                            "min-w-[300px] w-[300px] flex flex-col rounded-2xl bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl border border-white/60 dark:border-white/10 shadow-md",
                            snap.isDragging && "ring-2 ring-blue-400/60"
                          )}
                          style={{ ...prov.draggableProps.style, maxHeight: "calc(100vh - 200px)" }}
                          data-testid={`list-column-${list.list_id}`}
                        >
                          <div
                            {...prov.dragHandleProps}
                            className="flex items-center justify-between p-3 cursor-grab active:cursor-grabbing"
                          >
                            <ListNameEditor list={list} onRename={(name) => renameListMut.mutate({ listId: list.list_id, name })} />
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button data-testid={`list-menu-${list.list_id}`} className="h-7 w-7 grid place-items-center rounded-md hover:bg-white/40 dark:hover:bg-white/10">
                                  <MoreHorizontal className="h-4 w-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="glass-panel">
                                <DropdownMenuItem
                                  className="text-rose-500"
                                  onClick={() => { if (window.confirm(t("board.deleteListConfirm"))) deleteListMut.mutate(list.list_id); }}
                                  data-testid={`list-delete-${list.list_id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" /> {t("board.deleteList")}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>

                          <Droppable droppableId={list.list_id} type="CARD">
                            {(p2, s2) => (
                              <div
                                ref={p2.innerRef} {...p2.droppableProps}
                                className={cn(
                                  "px-3 pb-2 flex-1 overflow-y-auto kanban-scroll min-h-[20px] transition-colors",
                                  s2.isDraggingOver && "bg-blue-500/10"
                                )}
                              >
                                {listCards.map((card, ci) => (
                                  <Draggable draggableId={card.card_id} index={ci} key={card.card_id}>
                                    {(p3, s3) => (
                                      <div
                                        ref={p3.innerRef} {...p3.draggableProps} {...p3.dragHandleProps}
                                        className={cn("mb-2", s3.isDragging && "card-dragging")}
                                        data-testid={`card-${card.card_id}`}
                                      >
                                        <CardTile card={card} labels={labels} onClick={() => setOpenCard(card.card_id)} />
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {p2.placeholder}
                              </div>
                            )}
                          </Droppable>

                          <CardComposer
                            onAdd={(title) => createCardMut.mutate({ listId: list.list_id, title })}
                            data-testid={`list-add-card-${list.list_id}`}
                          />
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}

                {/* Add list */}
                <div className="min-w-[300px] w-[300px] flex-none">
                  {addingList ? (
                    <div className="rounded-2xl p-3 bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl border border-white/60 dark:border-white/10">
                      <Input
                        autoFocus value={newListName} onChange={(e) => setNewListName(e.target.value)}
                        placeholder={t("board.listName")} data-testid="new-list-name"
                        className="bg-white/70 dark:bg-slate-900/40"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && newListName.trim()) createListMut.mutate(newListName.trim());
                          if (e.key === "Escape") { setAddingList(false); setNewListName(""); }
                        }}
                      />
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          onClick={() => newListName.trim() && createListMut.mutate(newListName.trim())}
                          data-testid="new-list-submit"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >{t("board.addList")}</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setAddingList(false); setNewListName(""); }}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      data-testid="add-list-button"
                      onClick={() => setAddingList(true)}
                      className="w-full rounded-2xl p-3 text-left text-sm font-medium bg-white/30 dark:bg-slate-900/30 hover:bg-white/50 dark:hover:bg-slate-900/50 backdrop-blur border border-white/40 dark:border-white/10 transition flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" /> {t("board.addList")}
                    </button>
                  )}
                </div>
              </div>
            )}
          </Droppable>
        </DragDropContext>
        )}
      </div>

      {openCard && (
        <CardModal
          cardId={openCard}
          boardId={boardId}
          labels={labels}
          onClose={() => { setOpenCard(null); if (searchParams.get("card")) { setSearchParams({}); } }}
        />
      )}

      <ActivitySheet boardId={boardId} open={activityOpen} onClose={() => setActivityOpen(false)} />
      <ShareBoardDialog boardId={boardId} open={shareOpen} onOpenChange={setShareOpen} />
      <BoardNotifSettingsDialog boardId={boardId} open={notifOpen} onOpenChange={setNotifOpen} />
      <BoardDetailsDialog boardId={boardId} open={detailsOpen} onOpenChange={setDetailsOpen} />

      {/* ─── NadIA floating button + panel ─── */}
      <button
        onClick={() => setNadiaOpen(true)}
        data-testid="nadia-open-btn"
        aria-label="Preguntar a NadIA"
        title="NadIA · Asistente de Proyecto"
        className="fixed bottom-6 right-6 z-30 h-14 w-14 rounded-full grid place-items-center shadow-xl shadow-blue-500/40 hover:shadow-2xl hover:shadow-violet-500/50 hover:scale-105 transition-all group"
        style={{ background: "conic-gradient(from 210deg, #3b82f6, #8b5cf6, #ec4899, #3b82f6)" }}
      >
        <div className="absolute inset-0.5 rounded-full bg-slate-950 grid place-items-center">
          <Sparkles className="h-6 w-6 text-blue-300 group-hover:text-white transition-colors" />
        </div>
        <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-400 animate-pulse ring-2 ring-slate-950" />
      </button>
      {nadiaOpen && <NadIAPanel boardId={boardId} onClose={() => setNadiaOpen(false)} />}
    </div>
  );
}

function ListNameEditor({ list, onRename }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(list.name);
  useEffect(() => setName(list.name), [list.name]);
  if (editing) {
    return (
      <Input
        autoFocus value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => { setEditing(false); if (name.trim() && name !== list.name) onRename(name.trim()); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { setEditing(false); if (name.trim() && name !== list.name) onRename(name.trim()); }
          if (e.key === "Escape") { setEditing(false); setName(list.name); }
        }}
        className="h-8 bg-white/70 dark:bg-slate-900/40"
        data-testid={`list-rename-${list.list_id}`}
      />
    );
  }
  return (
    <button onClick={() => setEditing(true)} className="text-left font-semibold flex-1 truncate" data-testid={`list-name-${list.list_id}`}>
      {list.name}
    </button>
  );
}

function CardComposer({ onAdd }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const { t } = useTranslation();

  if (editing) {
    return (
      <div className="p-3">
        <Input
          autoFocus value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder={t("board.cardTitlePh")}
          className="bg-white/70 dark:bg-slate-900/40"
          data-testid="card-composer-input"
          onKeyDown={(e) => {
            if (e.key === "Enter" && title.trim()) { onAdd(title.trim()); setTitle(""); setEditing(false); }
            if (e.key === "Escape") { setTitle(""); setEditing(false); }
          }}
        />
        <div className="flex gap-2 mt-2">
          <Button
            size="sm"
            data-testid="card-composer-submit"
            onClick={() => { if (title.trim()) { onAdd(title.trim()); setTitle(""); setEditing(false); } }}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >{t("board.addCardBtn")}</Button>
          <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setTitle(""); }}><X className="h-4 w-4" /></Button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="m-3 mt-1 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-white/40 dark:hover:bg-white/10 text-left flex items-center gap-2"
      data-testid="card-composer-trigger"
    >
      <Plus className="h-4 w-4" /> {t("board.addCard")}
    </button>
  );
}
